import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function getSessionId() {
  let id = localStorage.getItem("blf_chat_session");
  if (!id) {
    id = "chat-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("blf_chat_session", id);
  }
  return id;
}

const GREETING = {
  fr: "Bonjour ! Je suis l'assistant Buy Likes and Followers. Une question sur nos services, une commande ou la garantie ? Je suis là pour vous aider.",
  en: "Hi! I'm the Buy Likes and Followers assistant. Any question about our services, an order, or the guarantee? I'm here to help.",
};
const PLACEHOLDER = { fr: "Écrivez votre message…", en: "Type your message…" };
const TITLE = { fr: "Assistant", en: "Assistant" };
const SUBTITLE = { fr: "En ligne · répond en quelques secondes", en: "Online · replies in seconds" };

export default function ChatWidget() {
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "fr";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING[lang] }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/chatbot/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, language: lang }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", text: next[next.length - 1].text + (delta || "") };
              return next;
            });
          } catch { /* ignore parse errors on partial chunks */ }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", text: lang === "fr" ? "Oups, une erreur est survenue. Réessayez ou contactez support@buylikesandfollowers.com." : "Oops, something went wrong. Please retry or contact support@buylikesandfollowers.com." };
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60]" data-testid="chat-widget-root">
      {open && (
        <div
          className="mb-3 w-[340px] sm:w-[368px] h-[480px] rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10 bg-gradient-to-r from-indigo-600/20 to-fuchsia-600/20">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#6366f1,#a855f7)" }}>
              <Sparkles className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white leading-tight">{TITLE[lang]}</p>
              <p className="text-[11px] text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />{SUBTITLE[lang]}</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label={lang === "fr" ? "Fermer le chat" : "Close chat"} className="text-zinc-500 hover:text-white transition-colors" data-testid="chat-widget-close-btn">
              <X className="w-4.5 h-4.5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-widget-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "bg-white/8 text-zinc-200 rounded-bl-sm border border-white/5"
                  }`}
                  data-testid={m.role === "user" ? `chat-message-user-${i}` : `chat-message-assistant-${i}`}
                >
                  {m.text || (loading && i === messages.length - 1 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={PLACEHOLDER[lang]}
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-indigo-500/50"
              data-testid="chat-widget-input"
            />
            <button
              onClick={send} disabled={loading || !input.trim()}
              aria-label={lang === "fr" ? "Envoyer" : "Send"}
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 disabled:opacity-40 transition-transform hover:scale-105"
              style={{ background: "linear-gradient(135deg,#6366f1,#a855f7)" }}
              data-testid="chat-widget-send-btn"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105 relative"
        style={{ background: "linear-gradient(135deg,#6366f1,#a855f7)", boxShadow: "0 8px 28px -6px rgba(99,102,241,0.6)" }}
        data-testid="chat-widget-toggle-btn"
        aria-label="Chat"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
        {!open && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-zinc-950" />}
      </button>
    </div>
  );
}
